
import { useState } from 'react';

type CalcType = 'ccr' | 'egfr' | 'bmi' | 'ibw' | 'bsa';

interface CalcResult {
  label: string;
  value: string;
  unit: string;
  note?: string;
}

export default function PharmacyCalculator() {
  const [activeCalc, setActiveCalc] = useState<CalcType>('ccr');
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [scr, setScr] = useState('');
  const [results, setResults] = useState<CalcResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  const calcTabs: { key: CalcType; label: string; short: string }[] = [
    { key: 'ccr', label: 'Cockcroft-Gault式', short: 'CCr' },
    { key: 'egfr', label: 'eGFR（日本人）', short: 'eGFR' },
    { key: 'bmi', label: 'BMI', short: 'BMI' },
    { key: 'ibw', label: '理想体重', short: 'IBW' },
    { key: 'bsa', label: '体表面積', short: 'BSA' },
  ];

  const needsField = (field: 'age' | 'weight' | 'height' | 'scr'): boolean => {
    const map: Record<CalcType, string[]> = {
      ccr: ['age', 'weight', 'scr'],
      egfr: ['age', 'scr'],
      bmi: ['weight', 'height'],
      ibw: ['height'],
      bsa: ['weight', 'height'],
    };
    return map[activeCalc].includes(field);
  };

  const needsGender = (): boolean => {
    return activeCalc === 'ccr' || activeCalc === 'egfr';
  };

  const calculate = () => {
    const a = parseFloat(age);
    const w = parseFloat(weight);
    const h = parseFloat(height);
    const s = parseFloat(scr);
    const newResults: CalcResult[] = [];

    switch (activeCalc) {
      case 'ccr': {
        if (isNaN(a) || isNaN(w) || isNaN(s) || s <= 0) return;
        let ccr = ((140 - a) * w) / (72 * s);
        let note = '男性';
        if (gender === 'female') {
          ccr *= 0.85;
          note = '女性（×0.85補正）';
        }
        newResults.push({
          label: 'CCr（クレアチニンクリアランス）',
          value: ccr.toFixed(1),
          unit: 'mL/min',
          note,
        });
        break;
      }
      case 'egfr': {
        if (isNaN(a) || isNaN(s) || s <= 0) return;
        let egfr = 194 * Math.pow(s, -1.094) * Math.pow(a, -0.287);
        let note = '男性';
        if (gender === 'female') {
          egfr *= 0.739;
          note = '女性（×0.739補正）';
        }
        newResults.push({
          label: 'eGFR（推算糸球体濾過量）',
          value: egfr.toFixed(1),
          unit: 'mL/min/1.73m\u00B2',
          note,
        });
        break;
      }
      case 'bmi': {
        if (isNaN(w) || isNaN(h) || h <= 0) return;
        const hm = h / 100;
        const bmi = w / (hm * hm);
        let category = '';
        if (bmi < 18.5) category = '低体重';
        else if (bmi < 25) category = '普通体重';
        else if (bmi < 30) category = '肥満（1度）';
        else if (bmi < 35) category = '肥満（2度）';
        else if (bmi < 40) category = '肥満（3度）';
        else category = '肥満（4度）';
        newResults.push({
          label: 'BMI（体格指数）',
          value: bmi.toFixed(1),
          unit: 'kg/m\u00B2',
          note: category,
        });
        break;
      }
      case 'ibw': {
        if (isNaN(h) || h <= 0) return;
        const hm = h / 100;
        const ibw = hm * hm * 22;
        newResults.push({
          label: '理想体重（IBW）',
          value: ibw.toFixed(1),
          unit: 'kg',
          note: '身長(m)\u00B2 \u00D7 22',
        });
        break;
      }
      case 'bsa': {
        if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return;
        const bsa = Math.sqrt((h * w) / 3600);
        newResults.push({
          label: '体表面積（BSA）',
          value: bsa.toFixed(4),
          unit: 'm\u00B2',
          note: 'Mosteller\u5F0F',
        });
        break;
      }
    }

    setResults(newResults);
    setShowResults(true);
  };

  const resetForm = () => {
    setAge('');
    setWeight('');
    setHeight('');
    setScr('');
    setResults([]);
    setShowResults(false);
  };

  const handleTabChange = (key: CalcType) => {
    setActiveCalc(key);
    setResults([]);
    setShowResults(false);
  };

  const formulaText: Record<CalcType, string> = {
    ccr: '(140 \u2212 \u5E74\u9F62) \u00D7 \u4F53\u91CD(kg) / (72 \u00D7 Scr)\u3000\u5973\u6027: \u00D7 0.85',
    egfr: '194 \u00D7 Scr^\u207B\u00B9\u00B7\u2070\u2079\u2074 \u00D7 \u5E74\u9F62^\u207B\u2070\u00B7\u00B2\u2078\u2077\u3000\u5973\u6027: \u00D7 0.739',
    bmi: '\u4F53\u91CD(kg) / \u8EAB\u9577(m)\u00B2',
    ibw: '\u8EAB\u9577(m)\u00B2 \u00D7 22',
    bsa: '\u221A[\u8EAB\u9577(cm) \u00D7 \u4F53\u91CD(kg) / 3600]',
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      {/* ヘッダー */}
      <div className="bg-gradient-to-r from-teal-500 to-emerald-500 px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
            <i className="ri-calculator-line text-white text-lg"></i>
          </div>
          <div>
            <h3 className="text-white font-bold text-sm">薬学計算ツール</h3>
            <p className="text-teal-100 text-xs">臨床で使える5つの計算式</p>
          </div>
        </div>
      </div>

      {/* タブ */}
      <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border-b border-gray-100">
        {calcTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
              activeCalc === tab.key
                ? 'bg-teal-500 text-white shadow-md'
                : 'bg-white text-gray-600 hover:bg-teal-50 hover:text-teal-600 border border-gray-200'
            }`}
          >
            {tab.short}
          </button>
        ))}
      </div>

      <div className="p-4">
        {/* 計算式表示 */}
        <div className="bg-gray-50 rounded-lg px-3 py-2 mb-3 border border-gray-100">
          <p className="text-xs text-gray-500 font-medium mb-0.5">{calcTabs.find(t => t.key === activeCalc)?.label}</p>
          <p className="text-xs text-gray-700 font-mono">{formulaText[activeCalc]}</p>
        </div>

        {/* 入力フォーム */}
        <div className="space-y-2.5">
          {/* 性別 */}
          {needsGender() && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-gray-600 w-12 shrink-0">性別</label>
              <div className="flex gap-2 flex-1">
                <button
                  onClick={() => setGender('male')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    gender === 'male'
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  男性
                </button>
                <button
                  onClick={() => setGender('female')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    gender === 'female'
                      ? 'bg-rose-400 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  女性
                </button>
              </div>
            </div>
          )}

          {/* 年齢 */}
          {needsField('age') && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-gray-600 w-12 shrink-0">年齢</label>
              <div className="flex-1 relative">
                <input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="例: 65"
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">歳</span>
              </div>
            </div>
          )}

          {/* 身長 */}
          {needsField('height') && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-gray-600 w-12 shrink-0">身長</label>
              <div className="flex-1 relative">
                <input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="例: 170"
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">cm</span>
              </div>
            </div>
          )}

          {/* 体重 */}
          {needsField('weight') && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-gray-600 w-12 shrink-0">体重</label>
              <div className="flex-1 relative">
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="例: 60"
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">kg</span>
              </div>
            </div>
          )}

          {/* Scr */}
          {needsField('scr') && (
            <div className="flex items-center gap-2">
              <label className="text-xs font-bold text-gray-600 w-12 shrink-0">Scr</label>
              <div className="flex-1 relative">
                <input
                  type="number"
                  step="0.01"
                  value={scr}
                  onChange={(e) => setScr(e.target.value)}
                  placeholder="例: 1.0"
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">mg/dL</span>
              </div>
            </div>
          )}
        </div>

        {/* ボタン */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={calculate}
            className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-500 text-white py-2 rounded-lg text-sm font-bold hover:from-teal-600 hover:to-emerald-600 transition-all shadow-md cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calculator-line mr-1"></i>
            計算
          </button>
          <button
            onClick={resetForm}
            className="px-4 bg-gray-100 text-gray-600 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
          >
            リセット
          </button>
        </div>

        {/* 結果表示 */}
        {showResults && results.length > 0 && (
          <div className="mt-3 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg p-3 border border-teal-200">
            {results.map((r, i) => (
              <div key={i}>
                <p className="text-xs text-teal-700 font-medium mb-1">{r.label}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-teal-800">{r.value}</span>
                  <span className="text-sm text-teal-600">{r.unit}</span>
                </div>
                {r.note && (
                  <p className="text-xs text-teal-600 mt-1 flex items-center gap-1">
                    <i className="ri-information-line"></i>
                    {r.note}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
